import type { TiptapToolbarProps } from "@circle-react-shared/uikit/TipTapBlockEditor/Toolbar";
import {
  getCurrentRange,
  showSlashCommandMenu,
} from "@circle-react-shared/uikit/TipTapBlockEditor/Toolbar/helpers";
import { EmojiPickerV4 } from "@circle-react-uikit/EmojiPickerV4";
import { Portal } from "@circle-react-uikit/Portal";
import { DrawerDivider } from "@circle-react/components/Mobile/drawers/components/DrawerDivider";
import { DrawerMenuItem } from "@circle-react/components/Mobile/drawers/components/DrawerMenuItem";
import { Drawer } from "@circleco/compass/components/Drawer";
import { IconButton } from "@circleco/compass/components/IconButton";
import { useState } from "react";

import { t } from "@/i18n-js/instance";

export const MobileToolbar = ({
  editor,
  portalId,
  disabledExtensions,
  showImagePickerModal,
  showFileUploadModal,
  showGiphyPickerModal,
  showPollModal,
  showVideoClipRecorder,
  showVoiceMessageModal,
}: TiptapToolbarProps) => {
  const [isOpen, setIsOpen] = useState(false);

  if (!editor || !portalId) return null;

  const range = () => ({ editor, range: getCurrentRange({ editor }) });
  const canLoadFiles = !disabledExtensions["file_loader"];
  // Drawer.Close doesn't dismiss when its child has its own onClick.
  const closeAnd = (action: () => void) => () => {
    setIsOpen(false);
    action();
  };

  return (
    <Portal selectorQuery={`#${portalId}`}>
      <IconButton
        icon="plus"
        variant="outline"
        size="md"
        aria-label={t("quick_post_v3.mobile_toolbar.open_menu")}
        data-testid="quick-post-mobile-toolbar-trigger"
        onClick={() => setIsOpen(true)}
      />
      <Drawer.Root open={isOpen} onOpenChange={setIsOpen} snapTo="half">
        {/* finalFocus: returning focus to the trigger on close would blur the editor right after Format/Link focus it. */}
        <Drawer.Content
          aria-label={t("quick_post_v3.mobile_toolbar.open_menu")}
          finalFocus={false}
        >
          <div className="flex flex-col">
            <DrawerMenuItem
              icon="text"
              label={t("quick_post_v3.mobile_toolbar.format")}
              onClick={closeAnd(() => showSlashCommandMenu({ editor }))}
            />
            <DrawerDivider />
            {!disabledExtensions["image"] && (
              <DrawerMenuItem
                icon="image"
                label={t("quick_post_v3.mobile_toolbar.image")}
                onClick={closeAnd(() => showImagePickerModal(range()))}
              />
            )}
            {canLoadFiles && (
              <DrawerMenuItem
                icon="square-play"
                label={t("quick_post_v3.mobile_toolbar.video")}
                onClick={closeAnd(() =>
                  showFileUploadModal({ ...range(), type: "video" }),
                )}
              />
            )}
            {!disabledExtensions["poll"] && (
              <DrawerMenuItem
                icon="chart"
                label={t("quick_post_v3.mobile_toolbar.poll")}
                onClick={closeAnd(() => showPollModal(range()))}
              />
            )}
            {!disabledExtensions["voice_message"] && (
              <DrawerMenuItem
                icon="microphone"
                label={t("quick_post_v3.mobile_toolbar.record_audio")}
                onClick={closeAnd(() => showVoiceMessageModal(range()))}
              />
            )}
            {canLoadFiles && (
              <DrawerMenuItem
                icon="paperclip"
                label={t("quick_post_v3.mobile_toolbar.attach_file")}
                onClick={closeAnd(() => showFileUploadModal(range()))}
              />
            )}
            {canLoadFiles && (
              <DrawerMenuItem
                icon="gif-square"
                label={t("quick_post_v3.mobile_toolbar.gif")}
                onClick={closeAnd(() => showGiphyPickerModal(range()))}
              />
            )}
            {!disabledExtensions["emojipicker"] && (
              <EmojiPickerV4
                as="div"
                targetWrapperClassName="w-full"
                onEmojiSelect={emojiData => {
                  if (!emojiData.emoji) return;
                  editor.chain().focus().insertContent(emojiData.emoji).run();
                  setIsOpen(false);
                }}
                target={
                  <DrawerMenuItem
                    keepOpen
                    icon="emoji-smiley"
                    label={t("quick_post_v3.mobile_toolbar.emoji")}
                  />
                }
              />
            )}
            {!disabledExtensions["entities"] && (
              <DrawerMenuItem
                icon="hashtag"
                label={t("quick_post_v3.mobile_toolbar.link")}
                onClick={closeAnd(() =>
                  editor.chain().focus().insertContent(" #").run(),
                )}
              />
            )}
            {!disabledExtensions["video_clip"] && (
              <DrawerMenuItem
                icon="video"
                label={t("quick_post_v3.mobile_toolbar.video_clip")}
                onClick={closeAnd(() => showVideoClipRecorder(range()))}
              />
            )}
          </div>
        </Drawer.Content>
      </Drawer.Root>
    </Portal>
  );
};
